import React, { useState, useEffect } from 'react';
import {
    GECKO_CONSTS,
    GeckoBtn,
    GeckoAlert,
    GeckoLoadingService,
    GeckoDialogService
} from '@geckolabs/gecko-react-ui';
import RSVP_CONSTS from '../../../../constants';
import { __, STR_KEY } from '../../../../services/Translate';
import { $stateParams } from '../../../../router';
import EngageRequest from '../../../../services/EngageRequest';
import { 
    AddAttendancePostMessage,
    ChangeAttendancePostMessage,
    CancelAttendancePostMessage 
} from '../../services/PostMessageEventTriggers';
import SessionTimeSelect from '../SessionTimeSelect';

const loader = new GeckoLoadingService();

const attendanceRequest = (sessionTimeId, params={}) => {
    loader.show();
    return EngageRequest.post(`/attendances/rsvp/${$stateParams.account_uuid}/${$stateParams.contact_uuid}/${sessionTimeId}`, params).finally(() => loader.hide());
};

const addAttendanceRequest = (sessionTimeId) => {
    return attendanceRequest(sessionTimeId, {attending: 'yes', registration_force_flags: ['closing_date']});
};

const cancelAttendanceRequest = (sessionTimeId) => {
    return attendanceRequest(sessionTimeId, {status: RSVP_CONSTS.ATTENDANCE.STATUS_CANCELLED});
};

const confirmWithUser = (text) => new GeckoDialogService().confirm(text);

const addFn = (sessionTimeId, updateDataCb, errorCb) => {
    return addAttendanceRequest(sessionTimeId).then(updateDataCb).then(({currentAttendances, previousAttendances, contact}) => {
        console.log('--- ATTENDANCE UPDATE---', currentAttendances, previousAttendances, contact);
        
        if (previousAttendances.length) {
            ChangeAttendancePostMessage(currentAttendances.concat(previousAttendances), contact);
        } else {
            AddAttendancePostMessage(currentAttendances, contact);
        }

        return {currentAttendances, previousAttendances, contact};
    }).catch(errorCb);
};

const cancelFn = (sessionTimeId, updateDataCb, errorCb, currentAttendance) => {
    return confirmWithUser('Are you sure you want to cancel this attendee for this session?').then((ok) => {
        if (ok) {
            return cancelAttendanceRequest(sessionTimeId).then(updateDataCb).then(({currentAttendances, contact}) => {
                console.log('--- ATTENDANCE CANCEL---', currentAttendances, contact);
                CancelAttendancePostMessage(currentAttendances, contact);
            }).catch(errorCb);
        }
    });
};

export const SessionSingleControls = ({session, sessionTimes, attendance, updateDataCb}) => {
    const [currentSessionTimeId, setCurrentSessionTimeId] = useState(null);
    const [nextSessionTimeId, setNextSessionTimeId] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        const currentSessionTimeId = attendance ? attendance.event_id : null;
        setCurrentSessionTimeId(currentSessionTimeId);
        /* Use session time from params to prefill dropdown or when NO attendance, prefill with first session time */
        let nextSessionTimeId;
        if ($stateParams.session_time_id) {
            nextSessionTimeId = parseInt($stateParams.session_time_id);
        } else {
            nextSessionTimeId = currentSessionTimeId || sessionTimes[0].id;
        }
        setNextSessionTimeId(nextSessionTimeId);
    }, [attendance]);

    const controls = (() => {

        /* DONT show any controls when the attendee has ALREADY been checked-in at the session. */
        const attendeeHasAttended = [RSVP_CONSTS.ATTENDANCE.STATUS_ATTENDED, RSVP_CONSTS.ATTENDANCE.STATUS_ENGAGED, RSVP_CONSTS.ATTENDANCE.STATUS_DID_NOT_ATTEND].includes(attendance ? attendance.status : null);
        if (attendeeHasAttended) {
            return (
                <GeckoAlert type="warning" icon="exclamation-circle">
                    The attendee has already attended this session.
                </GeckoAlert>
            );
        };

        const showAddBtn    = !attendance || [RSVP_CONSTS.ATTENDANCE.STATUS_CANCELLED, RSVP_CONSTS.ATTENDANCE.STATUS_REMOVED].includes(attendance ? attendance.status : null);
        const showChangeBtn = (() => {
            if (sessionTimes.length > 1) {
                const sessionTimeCanBeChanged   = attendance && [RSVP_CONSTS.ATTENDANCE.STATUS_INVITED, RSVP_CONSTS.ATTENDANCE.STATUS_REGISTERED, RSVP_CONSTS.ATTENDANCE.STATUS_WAITLISTED].includes(attendance.status);
                const sessionTimeHasBeenChanged = currentSessionTimeId !== nextSessionTimeId;
                return sessionTimeCanBeChanged && sessionTimeHasBeenChanged;
            }
            return false;
        })();
        const showCancelBtn = attendance && [RSVP_CONSTS.ATTENDANCE.STATUS_INVITED, RSVP_CONSTS.ATTENDANCE.STATUS_REGISTERED, RSVP_CONSTS.ATTENDANCE.STATUS_WAITLISTED].includes(attendance.status);

        return (
            <>  
                {sessionTimes.length > 1 ? (
                   <SessionTimeSelect {...{
                        sessionTimes, 
                        sessionTimeId: nextSessionTimeId, 
                        setSessionTimeId: setNextSessionTimeId
                    }} /> 
                ) : null}
                
                {showAddBtn ? (
                    <GeckoBtn {...{
                        name: 'Add to Session',
                        type: GECKO_CONSTS.BTN_TYPE_PRIMARY,
                        icon: 'plus-circle',
                        handler: addFn.bind(this, nextSessionTimeId, updateDataCb, setError)
                    }} />
                ) : null}

                {showChangeBtn ? (
                    <GeckoBtn {...{
                        name: 'Change Session Time',
                        type: GECKO_CONSTS.BTN_TYPE_PRIMARY,
                        icon: 'pencil',
                        handler: addFn.bind(this, nextSessionTimeId, updateDataCb, setError)
                    }} />
                ) : null}

                {showCancelBtn ? (
                    <GeckoBtn {...{
                        name: __(STR_KEY.MODAL_SESSION_BTN_CANCEL),
                        type: GECKO_CONSTS.BTN_TYPE_DANGER,
                        icon: 'times',
                        handler: cancelFn.bind(this, currentSessionTimeId, updateDataCb, setError, attendance)
                    }} />
                ) : null}
                
            </>
        );
    })();

    return (
        <>
            {error ? (
                <div className="rsvp-lite-card">
                    <GeckoAlert type="warning" icon="exclamation-circle" >
                        {String(error)}
                    </GeckoAlert>
                </div>
            ) : null}

            <div className="rsvp-lite-card">
                {controls}
            </div>
        </>
    );
};

export default SessionSingleControls;